import { SlicePipe } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import { Component, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { ButtonComponent } from '@components/buttons/button/button.component';
import { IconButtonComponent } from '@components/buttons/icon-button/icon-button.component';
import { CountdownReleaseComponent } from '@components/countdown/countdown-release/countdown-release.component';
import {
  AccessabilityDialogComponent,
  MustBeLoginData,
} from '@components/dialogs/accessability-dialog/accessability-dialog.component';
import {
  Episode,
  EpisodeReleaseStatus,
  InterestAction,
  InterestType,
  PlayableEpisode,
  ProgramWithSeasons,
} from '@shared/graphql/api.generated';
import { SnackbarService, SnackbarType } from '@shared/helper/snackbar.service';
import { AnalyticsService } from '@shared/services/analytics.service';
import { EpisodeService } from '@shared/services/episode.service';
import { GeneralService } from '@shared/services/general.service';
import { InterestService } from '@shared/services/interest.service';
import { ProgramService } from '@shared/services/program.service';
import { TokenService } from '@shared/services/token.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-product-info-mobile',
  imports: [
    ButtonComponent,
    IconButtonComponent,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    CountdownReleaseComponent,
    SlicePipe,
  ],
  providers: [AnalyticsService, ProgramService, InterestService],
  templateUrl: './product-info-mobile.component.html',
  styleUrl: './product-info-mobile.component.scss',
})
export class ProductInfoMobileComponent {
  ProgramData = input.required<ProgramWithSeasons>();
  Episode = input<Episode>();
  ProgramHasCopyright = input<boolean>();
  summaryLength = signal(0);
  showSummaryLength = signal(60);
  programBookmarked = signal(false);
  isShowDllBox = signal(false);
  submitInterest = signal(false);
  selectedInterest = signal<InterestAction | null>(null);
  isShowMore = true;
  interestAction = InterestAction;
  episodeReleaseStatus = EpisodeReleaseStatus;

  tokenService = inject(TokenService);
  programService = inject(ProgramService);
  private generalService = inject(GeneralService);
  private analyticsService = inject(AnalyticsService);
  private interestService = inject(InterestService);
  private episodeService = inject(EpisodeService);
  private snackbar = inject(SnackbarService);
  private dialog = inject(MatDialog);

  ngOnChanges() {
    this.handleSummaryLength();
    this.programBookmarked.set(this.ProgramData().program.isBookMarkedByMe);
    this.selectedInterest.set(
      this.Episode()
        ? this.Episode()!.interestDetails?.interestedByUser!
        : this.ProgramData().program.interestDetails?.interestedByUser!,
    );
  }

  handleNavigateToAboutSection() {
    if (this.generalService.isBrowserOnly) {
      const aboutSection = document.getElementById('product-about-container');
      if (aboutSection) {
        aboutSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }

  handleShowMoreSummary() {
    this.isShowMore = !this.isShowMore;
    if (this.isShowMore) {
      this.showSummaryLength.set(60);
    } else {
      this.showSummaryLength.set(this.summaryLength());
    }
  }

  handleSeasonTitle() {
    return this.ProgramData().seasons.filter((item) => item?.id === this.Episode()?.seasonID)[0]
      ?.title;
  }

  handleSummaryLength() {
    if (this.Episode()) {
      this.summaryLength.set(this.Episode()!.summary.length);
    } else {
      this.summaryLength.set(this.ProgramData().program.summary.length);
    }
  }

  handlePlay() {
    this.getResumeOfProgram().subscribe({
      next: (data) => {
        if (this.episodeService.checkEpisodeDisplay(data)) {
          this.generalService.router.navigateByUrl(`/w/${data.uid}`);
        }
      },
    });
  }

  handleLoginAndWatch() {
    this.generalService.router.navigate(['/auth'], {
      queryParams: {
        ru: `/p/${this.ProgramData().program.uid}`,
      },
    });
  }

  private getResumeOfProgram() {
    let resumeEpisode: Episode | PlayableEpisode;
    return new Observable<Episode | PlayableEpisode>((observer) => {
      this.analyticsService.getResumeOfProgram(this.ProgramData().program.id).subscribe({
        next: (res: any) => {
          if (res?.episode_id) {
            this.episodeService.getEpisodeById(res.episode_id).subscribe({
              next: (res) => {
                resumeEpisode = res.data!;
                observer.next(resumeEpisode);
                observer.complete();
              },
            });
          } else {
            if (this.ProgramData()) {
              resumeEpisode = this.ProgramData().solitaryEpisode!;
              observer.next(resumeEpisode);
              observer.complete();
            }
          }
        },
        error: (e) => {
          if (e.status === HttpStatusCode.Unauthorized) {
            this.tokenService.refreshToken().subscribe({
              next: () => {
                this.getResumeOfProgram();
              },
            });
          } else {
            if (this.ProgramData()) {
              resumeEpisode = this.ProgramData().solitaryEpisode!;
              observer.next(resumeEpisode);
              observer.complete();
            }
          }
        },
      });
    });
  }

  toggleBookmarkProgram() {
    if (this.tokenService.getRtk()) {
      if (this.ProgramData()) {
        this.programService.toggleBookmark(this.ProgramData().program.id).subscribe({
          next: (res) => {
            this.programBookmarked.update((value) => !value);
            this.snackbar.showSnackbar(res.status.message);
          },
          error: (e) => {
            this.snackbar.showSnackbar(e.networkError.message, SnackbarType.error);
          },
        });
      }
    } else {
      const data: MustBeLoginData = {
        message: 'برای اضافه کردن به لیست من، ابتدا باید وارد شوید',
      };
      this.dialog.open(AccessabilityDialogComponent, {
        data,
      });
    }
  }

  handleInterestAction(action: InterestAction) {
    this.submitInterest.set(true);
    if (this.selectedInterest() === action) {
      this.removeInterest();
    } else {
      this.addInterest(action);
    }
  }

  addInterest(action: InterestAction) {
    if (this.tokenService.getRtk()) {
      const id = this.Episode() ? this.Episode()!.id : this.ProgramData().program.id;
      this.interestService
        .addInterest(id, action, this.Episode() ? InterestType.Episode : InterestType.Program)
        .subscribe({
          next: (res) => {
            this.submitInterest.set(false);
            this.snackbar.showSnackbar(res.status.message);
            if (res.status.statusCode === HttpStatusCode.Ok) {
              this.selectedInterest.set(action);
              this.isShowDllBox.set(false);
            }
          },
          error: (e) => {
            this.submitInterest.set(false);
            this.snackbar.showSnackbar(e.networkError.message, SnackbarType.error);
          },
        });
    } else {
      this.submitInterest.set(false);
      const data: MustBeLoginData = {
        message: 'برای ثبت علاقه‌مندی، ابتدا باید وارد شوید',
      };
      this.dialog.open(AccessabilityDialogComponent, {
        data,
      });
    }
  }

  removeInterest() {
    const id = this.Episode() ? this.Episode()!.id : this.ProgramData().program.id;
    this.interestService
      .removeInterest(id, this.Episode() ? InterestType.Episode : InterestType.Program)
      .subscribe({
        next: (res) => {
          this.submitInterest.set(false);
          this.snackbar.showSnackbar(res.status.message);
          if (res.status.statusCode === HttpStatusCode.Ok) {
            this.selectedInterest.set(null);
            this.isShowDllBox.set(false);
          }
        },
        error: (e) => {
          this.submitInterest.set(false);
          this.snackbar.showSnackbar(e.networkError.message, SnackbarType.error);
        },
      });
  }

  shareItem() {
    if (this.Episode()) {
      this.generalService.shareItem(this.Episode()!.title, this.Episode()!.summary);
    } else {
      this.generalService.shareItem(
        this.ProgramData().program.title,
        this.ProgramData().program.summary,
      );
    }
  }
}
